import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { QUESTION_STEP_TYPES } from './step.schema.js';
extendZodWithOpenApi(z);
const uuidSchema = z.string().uuid('Invalid UUID format');
// === Request Schemas ===
export const CreateLearnSessionSchema = z
    .object({
    lessonId: uuidSchema.openapi({
        description: 'ID of the lesson to start a learn session for',
        example: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
    }),
    courseId: uuidSchema.optional().openapi({
        description: 'Optional course ID to associate with the session',
        example: 'a0b1c2d3-e4f5-6789-abcd-ef0123456789',
    }),
})
    .openapi('CreateLearnSessionBody');
export const CreateReviseSessionSchema = z
    .object({
    lessonIds: z
        .array(z.string().uuid())
        .openapi({
        description: 'List of lesson IDs to revise',
        example: ['b1a2c3d4-e5f6-7890-abcd-ef1234567890'],
    }),
    questionTypes: z
        .array(z.enum(QUESTION_STEP_TYPES))
        .optional()
        .openapi({
        description: 'Optional filter for question types to include in the revision session',
        example: ['mcq', 'true_false'],
    }),
    maxQuestions: z.number().int().min(1).max(100).optional().openapi({
        description: 'Maximum number of questions to include (1-100)',
        example: 20,
    }),
    stepVersionIds: z
        .array(z.string().uuid())
        .optional()
        .openapi({
        description: 'Pre-selected step version IDs from AI chatbot selection',
    }),
    nodeIds: z
        .array(z.string().uuid())
        .optional()
        .openapi({
        description: 'Curriculum node IDs to revise. Server resolves these to the learner-revisable step versions tagged to those nodes (including descendants). Used by the CoachCard to launch topic-scoped sessions from the dashboard.',
    }),
})
    .refine((data) => (data.stepVersionIds && data.stepVersionIds.length > 0) ||
    (data.nodeIds && data.nodeIds.length > 0) ||
    data.lessonIds.length > 0, {
    message: 'Either stepVersionIds, nodeIds, or at least one lessonId must be provided',
    path: ['lessonIds'],
})
    .openapi('CreateReviseSessionBody');
export const ReviseHistoryQuerySchema = z.object({
    page: z.string().optional().default('1').transform(Number),
    pageSize: z.string().optional().default('10').transform(Number),
});
export const SessionIdParamSchema = z
    .object({
    sessionId: uuidSchema.openapi({
        description: 'Unique identifier of the study session',
        example: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    }),
})
    .openapi('SessionIdParam');
export const UpdatePositionSchema = z
    .object({
    currentPosition: z.number().int().nonnegative().openapi({
        description: 'Zero-based index of the current step position in the session',
        example: 3,
    }),
})
    .openapi('UpdatePositionBody');
export const SubmitResponseParamSchema = z.object({
    sessionId: uuidSchema.openapi({
        description: 'Unique identifier of the study session',
        example: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    }),
    position: z
        .string()
        .transform((val) => parseInt(val, 10))
        .refine((val) => !isNaN(val) && val >= 0, 'Position must be a non-negative integer'),
});
export const SubmitResponseBodySchema = z
    .object({
    responseJson: z.unknown().openapi({
        description: 'The learner response payload (shape depends on question type)',
        example: { selected_option: 2 },
    }),
    responseType: z.string().optional().openapi({
        description: 'Type of the response (e.g. mcq, text, true_false)',
        example: 'mcq',
    }),
    responseTimeMs: z.number().int().nonnegative().optional().openapi({
        description: 'Active time from step entry to submit in milliseconds (time to answer)',
        example: 12500,
    }),
    clientSubmissionId: z.string().optional().openapi({
        description: 'Client-generated UUID for idempotent submission',
        example: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
    }),
    deviceId: z.string().optional().openapi({
        description: 'Identifier of the device the learner is using',
        example: 'device-abc-123',
    }),
    clientTs: z.string().optional().openapi({
        description: 'ISO 8601 timestamp from the client when the response was submitted',
        example: '2026-02-27T14:30:00.000Z',
    }),
})
    .openapi('SubmitResponseBody');
export const GetActiveSessionQuerySchema = z.object({
    include: z.string().optional().openapi({
        description: 'Comma-separated includes: steps, responses',
        example: 'steps,responses',
    }),
});
// === Response Schemas (for OpenAPI docs) ===
export const StudySessionSchema = z
    .object({
    id: uuidSchema.openapi({
        description: 'Unique identifier of the study session',
        example: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    }),
    userId: uuidSchema.openapi({
        description: 'ID of the user who owns this session',
        example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    }),
    studySessionType: z.enum(['learn', 'revise']).openapi({
        description: 'Whether this is a learn or revise session',
        example: 'learn',
    }),
    sourceLessonId: uuidSchema.nullable().openapi({
        description: 'ID of the lesson this session is based on (null for multi-lesson revise)',
        example: 'b1a2c3d4-e5f6-7890-abcd-ef1234567890',
    }),
    sourceLessonVersionId: uuidSchema.nullable().openapi({
        description: 'ID of the specific lesson version snapshot used',
        example: 'd4e5f6a7-b8c9-0123-def4-567890123456',
    }),
    status: z.enum(['active', 'completed', 'abandoned']).openapi({
        description: 'Current status of the session',
        example: 'active',
    }),
    startedAt: z.string().openapi({
        description: 'ISO 8601 timestamp when the session was started',
        example: '2026-02-27T10:00:00.000Z',
    }),
    lastActiveAt: z.string().openapi({
        description: 'ISO 8601 timestamp of the last activity in this session',
        example: '2026-02-27T10:15:00.000Z',
    }),
    completedAt: z.string().nullable().openapi({
        description: 'ISO 8601 timestamp when the session was completed (null if still active)',
        example: '2026-02-27T10:30:00.000Z',
    }),
    currentPosition: z.number().int().openapi({
        description: 'Zero-based index of the current step position',
        example: 3,
    }),
    configJson: z
        .unknown()
        .nullable()
        .openapi({
        description: 'Session configuration (e.g. selected question types, max questions)',
        example: { max_questions: 20 },
    }),
    scoreRaw: z.number().nullable().openapi({
        description: 'Raw score achieved in the session',
        example: 8,
    }),
    scorePct: z.number().nullable().openapi({
        description: 'Score as a percentage (0-100)',
        example: 80.0,
    }),
    completionPct: z.number().nullable().openapi({
        description: 'Percentage of steps completed (0-100)',
        example: 100.0,
    }),
    questionsCorrect: z.number().nullable().openapi({
        description: 'Number of questions answered correctly',
        example: 8,
    }),
    questionsTotal: z.number().nullable().openapi({
        description: 'Total number of questions in the session',
        example: 10,
    }),
    passed: z.boolean().nullable().openapi({
        description: 'Whether the learner passed the session (null if not yet completed or info-only)',
        example: true,
    }),
    passPct: z.number().nullable().openapi({
        description: 'Pass mark percentage required (0-100), null if no pass mark set',
        example: 50,
    }),
})
    .openapi('StudySession');
export const SubmitResponseResultSchema = z
    .object({
    response: z.unknown().openapi({
        description: 'The stored response data as submitted by the learner',
        example: { selected_option: 2 },
    }),
    isCorrect: z.boolean().openapi({
        description: 'Whether the learner answered correctly',
        example: true,
    }),
    score: z.number().openapi({
        description: 'Score awarded for this response',
        example: 1,
    }),
    maxScore: z.number().openapi({
        description: 'Maximum possible score for this step',
        example: 1,
    }),
    explanation: z.string().nullable().openapi({
        description: 'Explanation of the correct answer (may be null if not provided)',
        example: 'The correct answer is option B because...',
    }),
    feedback: z.string().nullable().optional().openapi({
        description: 'Optional additional feedback for the learner',
        example: 'Great job! You selected the right answer.',
    }),
})
    .openapi('SubmitResponseResult');
// === Session Feedback ===
/**
 * Body for POST /me/study-sessions/revise/preview-scope.
 * Given a set of curriculum node IDs, returns the question-type breakdown +
 * total revisable step count under those subtrees, scoped to the learner's
 * library. Powers the "By topic" tab's QuestionTypeFilter so it can show
 * real counts before the learner starts a session.
 */
export const PreviewReviseScopeBodySchema = z
    .object({
    nodeIds: z.array(uuidSchema).min(1).max(100).openapi({
        description: 'Curriculum node IDs to preview. Subtree-expanded server-side.',
    }),
})
    .openapi('PreviewReviseScopeBody');
export const PreviewReviseScopeResponseSchema = z
    .object({
    types: z
        .array(z.object({
        type: z.string().openapi({ example: 'mcq' }),
        count: z.number().int().openapi({ example: 42 }),
    }))
        .openapi({
        description: 'Per-question-type revisable step counts under the requested subtree, excluding info steps.',
    }),
    total: z.number().int().openapi({
        description: 'Sum of all type counts — the maximum slider value for the session.',
        example: 87,
    }),
})
    .openapi('PreviewReviseScopeResponse');
export const SubmitSessionFeedbackSchema = z
    .object({
    rating: z.number().int().min(1).max(5).optional().openapi({
        description: 'Star rating from 1 (worst) to 5 (best)',
        example: 4,
    }),
    feedback: z
        .string()
        .trim()
        .min(1)
        .max(2000)
        .optional()
        .openapi({
        description: 'Optional text feedback about the session',
        example: 'Really enjoyed this lesson!',
    }),
})
    .refine((data) => data.rating !== undefined || data.feedback !== undefined, {
    message: 'At least one of rating or feedback must be provided',
})
    .openapi('SubmitSessionFeedback');
//# sourceMappingURL=study-session.schema.js.map